import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ListTree } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PublicDecisionSheetView } from '@apm/contracts';
import { useAuth } from '../features/auth/auth-context';
import { decisionSheetKeys, publicDecisionApi } from '../features/decision-sheets/api';
import { PublicDecisionSheet } from '../features/decision-sheets/components/public-decision-sheet';
import { decisionSections } from '../features/decision-sheets/model';
import {
  PublicSheetLoading,
  DecisionOutlinePanel,
  OUTLINE_PREF_KEY,
  readOutlinePref,
  useDisplayFont,
  useGlassGlow,
  useWide,
} from '../features/decision-sheets/components/public-shell-parts';
import '../styles/public-shell.css';

/**
 * Link công khai của sổ chốt phương án — APM-DSX-b, plan §11.3.
 *
 * Nằm **ngoài** `_app` (khuôn `/s/$token`): người ngoài mở link khi chưa có tài
 * khoản, mà `_app` đá người chưa đăng nhập về `/login`. Cùng một đường API cho
 * hai trạng thái (`@Public()` + đọc user nếu có); `key={status}` dựng lại trang
 * khi trạng thái đăng nhập đổi để `viewer` đọc lại đúng người.
 *
 * ## Vỏ trang theo trang giới thiệu (2026-10-07)
 *
 * Người mở link thường là khách / BA bên ngoài — thứ đầu tiên họ thấy về
 * ChillPM là trang này, nên nó mặc cùng bộ đồ với `landing.html`: nền đốm màu
 * trôi, viên kính điều hướng có đĩa logo tròn, nội dung trong một tấm kính.
 * Phần ruột (`PublicDecisionSheet`) không đổi — thẻ câu hỏi vẫn là component
 * của app, vẫn theo sáng/tối của app; vỏ đổi theo `.dark` cùng nó.
 *
 * Mỗi khối (tiêu đề sổ, "Bạn là ai", ba ô tổng, từng thẻ câu) là **một tấm
 * kính riêng** — người dùng chốt 2026-10-07 — áp bằng CSS của vỏ theo
 * `data-testid` sẵn có, không sửa component của sổ.
 *
 * Font (Saira + Saira Semi Condensed) nạp **ở đây** chứ không ở `index.html`
 * để app không tải thêm font cho mọi màn hình chỉ vì một trang công khai.
 */
export const Route = createFileRoute('/d/$token')({ component: DecisionShareLinkPage });

function DecisionOutline({
  token,
  onPick,
}: {
  readonly token: string;
  readonly onPick: () => void;
}): ReactNode {
  const sheet = useQuery<PublicDecisionSheetView>({
    queryKey: decisionSheetKeys.public(token),
    queryFn: () => publicDecisionApi.get(token),
    enabled: false,
  });
  const sections = useMemo(
    () =>
      sheet.data
        ? decisionSections(
            {
              groups: sheet.data.groups,
              questions: sheet.data.questions.map((question) => ({
                ...question,
                raidItemId: null,
                raidAnswerRev: null,
              })),
            },
            'all',
          )
        : [],
    [sheet.data],
  );
  if (!sheet.data) return null;

  return <DecisionOutlinePanel sections={sections} onPick={onPick} />;
}

function DecisionShareLinkPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const { status, logout } = useAuth();
  useDisplayFont();
  useGlassGlow();

  const signedIn = status === 'authenticated';
  const wide = useWide();
  const [outlineOpen, setOutlineOpen] = useState(readOutlinePref);
  const toggleOutline = (): void =>
    setOutlineOpen((open) => {
      try {
        localStorage.setItem(OUTLINE_PREF_KEY, open ? 'closed' : 'open');
      } catch {
        // Không lưu được thì chỉ mất lựa chọn ở lần mở sau.
      }
      return !open;
    });
  const showOutline = outlineOpen && status !== 'restoring';

  return (
    <div className="public-shell text-foreground">
      <div className="public-shell__ambient" aria-hidden>
        <div className="public-shell__blob public-shell__blob--1" />
        <div className="public-shell__blob public-shell__blob--2" />
        <div className="public-shell__blob public-shell__blob--3" />
      </div>

      <nav className="public-shell__nav" aria-label={t('publicShell.navLabel')}>
        <div className="public-shell__pill public-shell__glass">
          {/* Người ngoài chưa biết app ⇒ logo về trang giới thiệu, như thanh trên của app. */}
          <a href="/landing.html" className="public-shell__brand" aria-label={t('common.appName')}>
            <img src="/chill-pm-avatar.png" alt="" width={512} height={512} />
          </a>
          <span className="public-shell__kicker">{t('decisionSheet.eyebrow')}</span>
          <button
            type="button"
            className="public-shell__toggle"
            aria-pressed={outlineOpen}
            aria-controls="decision-public-outline"
            onClick={toggleOutline}
            title={t(outlineOpen ? 'publicShell.outlineHide' : 'publicShell.outlineShow')}
          >
            <ListTree aria-hidden className="size-4" />
            <span className="public-shell__toggle-label">{t('publicShell.outline')}</span>
          </button>
          {signedIn ? (
            <a href="/projects" className="public-shell__cta">
              {t('publicShell.openApp')}
            </a>
          ) : (
            <a
              href={`/login?next=${encodeURIComponent(`/d/${token}`)}`}
              className="public-shell__cta"
            >
              {t('publicShell.tryIt')}
            </a>
          )}
        </div>
      </nav>

      <main
        className="public-shell__main"
        data-outline={showOutline ? (wide ? 'side' : 'float') : undefined}
      >
        {showOutline ? (
          <div id="decision-public-outline" className="public-shell__outline-slot">
            <DecisionOutline
              token={token}
              onPick={() => (wide ? undefined : setOutlineOpen(false))}
            />
          </div>
        ) : null}
        <div className="public-shell__sheet">
          {status === 'restoring' ? (
            <PublicSheetLoading />
          ) : (
            <PublicDecisionSheet
              key={status}
              token={token}
              onSwitchAccount={() => {
                // Đăng xuất xong mới sang trang đăng nhập — sang trước thì trang
                // ấy còn thấy phiên demo và đẩy thẳng về đây.
                void logout().finally(() =>
                  window.location.assign(`/login?next=${encodeURIComponent(`/d/${token}`)}`),
                );
              }}
            />
          )}
        </div>
      </main>

      <footer className="public-shell__footer">
        {t('decisionSheet.public.poweredBy')} · <a href="/landing.html">{t('publicShell.about')}</a>
      </footer>
    </div>
  );
}
